// src/pages/admin/Orders.tsx
//
// Staff order queue (M2-04). Staff review submitted order requests and
// confirm (hands off to the next FileMaker export) or cancel them.
// Every transition is audited server-side (order_status_history).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, X, RefreshCcw } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { authHeaders } from "@/lib/api";
import { useAuth } from "@/components/auth/AuthContext";
import { formatPrice } from "@/lib/formatPrice";
import { InvoicedSalesCard } from "./InvoicedSalesCard";

interface OrderLineRow {
  seq: number;
  product_id: number | null;
  label: string | null;
  quantity: number;
  line_total_tvac: number | null;
}
interface OrderRow {
  order_id: string;
  created_at: string | null;
  customer_id: number | null;
  customer_name_snapshot: string | null;
  status: string;
  note: string | null;
  total_tvac: number | null;
  exported_at: string | null;
  lines: OrderLineRow[];
}

// M2-06: fulfillment + demo shipping
interface ParcelRow {
  parcel_id: string;
  tracking_number: string;
  status: string;
  weight_kg: number | null;
  simulated: boolean;
}
interface FulfillmentRow {
  fulfillment_id: string;
  order_id: string;
  status: string;
  customer_name_snapshot: string | null;
  parcels: ParcelRow[];
}

const STATUS_VARIANT: Record<string, "default" | "warning" | "danger" | "success" | "teal"> = {
  new: "warning",
  confirmed: "teal",
  cancelled: "danger",
};

export function AdminOrders() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [fulfillments, setFulfillments] = useState<Record<string, FulfillmentRow>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      const [ro, rf] = await Promise.all([
        fetch("/api/orders", { headers: authHeaders() }),
        fetch("/api/fulfillments", { headers: authHeaders() }),
      ]);
      if (!ro.ok) throw new Error(`HTTP ${ro.status}`);
      const body = (await ro.json()) as { rows: OrderRow[] };
      setOrders(body.rows);
      if (rf.ok) {
        const fb = (await rf.json()) as { rows: FulfillmentRow[] };
        setFulfillments(Object.fromEntries(fb.rows.map((f) => [f.order_id, f])));
      }
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (auth.isReady && auth.isAuthenticated) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isReady, auth.isAuthenticated]);

  async function post(path: string, busyKey: string) {
    setBusy(busyKey);
    setError("");
    try {
      const r = await fetch(path, { method: "POST", headers: authHeaders() });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail ?? `HTTP ${r.status}`);
      }
      await refresh();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(null);
    }
  }

  async function transition(orderId: string, toStatus: string) {
    if (
      toStatus === "cancelled" &&
      !window.confirm(t("admin.orders.cancelConfirm", { order: orderId }))
    )
      return;
    setBusy(orderId);
    setError("");
    try {
      const r = await fetch(`/api/orders/${orderId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ to_status: toStatus }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail ?? `HTTP ${r.status}`);
      }
      await refresh();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(null);
    }
  }

  const pending = orders.filter((o) => o.status === "new");

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-xl font-510 tracking-tight text-fg-primary">
            {t("admin.orders.title")}
          </h1>
          <p className="mt-1 text-sm text-fg-tertiary">
            {t("admin.orders.subtitle")}
          </p>
        </div>
        <Badge variant={pending.length > 0 ? "warning" : "default"}>
          {t("admin.orders.awaitingReview", { count: pending.length })}
        </Badge>
      </header>

      {error && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {error}
        </div>
      )}

      {loading ? (
        <p className="py-8 text-center text-sm text-fg-tertiary">
          <RefreshCcw className="mr-2 inline h-4 w-4 animate-spin" />
          {t("admin.orders.loading")}
        </p>
      ) : orders.length === 0 ? (
        <EmptyState
          title={t("admin.orders.emptyTitle")}
          description={t("admin.orders.emptyDescription")}
        />
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <Card key={o.order_id}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-[10px] text-fg-secondary">
                    {o.order_id}
                  </code>
                  <Badge variant={STATUS_VARIANT[o.status] ?? "default"} size="sm">
                    {o.status}
                  </Badge>
                  {o.exported_at && (
                    <Badge variant="success" size="sm">
                    {t("admin.orders.exportedToFm")}
                  </Badge>
                  )}
                  <span className="text-[10px] text-fg-quaternary">
                    {o.created_at ? new Date(o.created_at).toLocaleString() : ""}
                  </span>
                  <span className="text-sm font-510 text-fg-primary">
                    {o.customer_name_snapshot ?? `customer #${o.customer_id}`}
                  </span>
                  <span className="ml-auto font-mono text-sm font-510 text-fg-primary">
                    {o.total_tvac != null ? formatPrice(o.total_tvac, "EUR") : "—"}
                  </span>
                  <div className="flex gap-1">
                    {o.status === "new" && (
                      <Button
                        size="sm"
                        disabled={busy === o.order_id}
                        onClick={() => transition(o.order_id, "confirmed")}
                      >
                        <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
                        {t("admin.orders.confirm")}
                      </Button>
                    )}
                    {(o.status === "new" || o.status === "confirmed") && !o.exported_at && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === o.order_id}
                        onClick={() => transition(o.order_id, "cancelled")}
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                        {t("admin.orders.cancel")}
                      </Button>
                    )}
                  </div>
                </div>
                {o.lines.length > 0 && (
                  <div className="mt-3 space-y-1 border-t border-border pt-3">
                    {o.lines.map((l) => (
                      <div key={l.seq} className="flex items-baseline gap-3 text-xs">
                        <code className="font-mono text-[10px] text-fg-quaternary">
                          {l.product_id ?? "?"}
                        </code>
                        <span className="flex-1 truncate text-fg-secondary">{l.label}</span>
                        <span className="font-mono text-fg-tertiary">x{Number(l.quantity)}</span>
                        <span className="font-mono text-fg-primary">
                          {l.line_total_tvac != null ? formatPrice(l.line_total_tvac, "EUR") : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {/* M2-06: fulfillment + DEMO shipping controls */}
                {o.status === "confirmed" && !fulfillments[o.order_id] && (
                  <div className="mt-3 border-t border-border pt-3">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === o.order_id}
                      onClick={() => post(`/api/orders/${o.order_id}/fulfill`, o.order_id)}
                    >
                      {t("admin.orders.startFulfillment")}
                    </Button>
                  </div>
                )}
                {fulfillments[o.order_id] && (
                  <FulfillmentBlock
                    f={fulfillments[o.order_id]!}
                    busy={busy}
                    onAdvance={(fid) => post(`/api/fulfillments/${fid}/advance`, fid)}
                    onShip={(fid) => post(`/api/fulfillments/${fid}/ship`, fid)}
                    onTrack={(pid) => post(`/api/parcels/${pid}/advance`, pid)}
                  />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Real sales activity while the Shop order pipeline fills up:
          the imported FileMaker invoice ledger (read-only, clearly not
          Shop order requests). */}
      <InvoicedSalesCard limit={10} />
    </div>
  );
}

// M2-06: fulfillment panel for one order (DEMO shipping, clearly marked)
function FulfillmentBlock({
  f,
  busy,
  onAdvance,
  onShip,
  onTrack,
}: {
  f: FulfillmentRow;
  busy: string | null;
  onAdvance: (fid: string) => void;
  onShip: (fid: string) => void;
  onTrack: (pid: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-3 space-y-2 border-t border-border pt-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-[10px] text-fg-secondary">
          {f.fulfillment_id}
        </code>
        <Badge variant={f.status === "delivered" ? "success" : "teal"} size="sm">
          {f.status}
        </Badge>
        <span className="text-[10px] uppercase tracking-wider text-fg-quaternary">
          {t("admin.orders.fulfillment")}
        </span>
        <span className="ml-auto flex gap-1">
          {(f.status === "pending" || f.status === "picking") && (
            <Button size="sm" variant="ghost" disabled={busy === f.fulfillment_id}
              onClick={() => onAdvance(f.fulfillment_id)}>
              {f.status === "pending"
                ? t("admin.orders.startPicking")
                : t("admin.orders.markPacked")}
            </Button>
          )}
          {f.status === "packed" && (
            <Button size="sm" disabled={busy === f.fulfillment_id}
              onClick={() => onShip(f.fulfillment_id)}>
              {t("admin.orders.shipDemo")}
            </Button>
          )}
        </span>
      </div>
      {f.parcels.map((p) => (
        <div key={p.parcel_id}
          className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-bg-canvas/40 px-3 py-2 text-xs">
          <span className="font-mono text-fg-primary">{p.tracking_number}</span>
          <Badge variant="default" size="sm">{p.status.replace(/_/g, " ")}</Badge>
          {p.weight_kg != null && (
            <span className="text-fg-quaternary">{Number(p.weight_kg).toFixed(1)} kg</span>
          )}
          <Badge variant="warning" size="sm">
            {t("admin.orders.demoSimulated")}
          </Badge>
          {p.status !== "delivered" && (
            <Button size="sm" variant="ghost" className="ml-auto"
              disabled={busy === p.parcel_id} onClick={() => onTrack(p.parcel_id)}>
              {t("admin.orders.nextScanDemo")}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
